import { useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Shield, ArrowLeft, Loader2, CheckCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await API.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center p-6 font-nunito" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#FF5C39] rounded-2xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" strokeWidth={3} />
          </div>
          <span className="text-2xl font-extrabold text-[#1F1F2E]">Alfie</span>
        </div>

        {sent ? (
          <div className="bg-white rounded-[32px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50 text-center">
            <CheckCircle className="w-16 h-16 text-[#34D399] mx-auto mb-4" strokeWidth={2} />
            <h2 className="text-2xl font-extrabold text-[#1F1F2E] mb-2" data-testid="reset-sent-heading">Check your email</h2>
            <p className="text-[#52526B] font-semibold mb-6">
              If <strong>{email}</strong> is registered, we've sent you a link to reset your password.
            </p>
            <Link
              to="/login"
              data-testid="back-to-login-link"
              className="inline-flex items-center gap-2 text-[#FF5C39] font-bold hover:underline"
            >
              <ArrowLeft className="w-4 h-4" /> Back to login
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold text-[#1F1F2E] tracking-tight" data-testid="forgot-password-heading">
              Forgot your password?
            </h1>
            <p className="text-[#52526B] font-semibold mt-2 mb-6">
              No worries! Enter your email and we'll send you a reset link.
            </p>

            {error && (
              <div className="bg-[#F43F5E]/10 text-[#F43F5E] px-4 py-3 rounded-2xl font-bold text-sm mb-4" data-testid="forgot-password-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1.5 block">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email-input"
                  className="w-full bg-[#FFF0E0]/50 border-2 border-transparent focus:border-[#4D96FF] focus:bg-white rounded-2xl px-4 py-3 text-base font-semibold outline-none transition-all placeholder:text-[#52526B]/50"
                  placeholder="parent@email.com"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit-button"
                className="w-full bg-[#FF5C39] text-white font-bold rounded-2xl px-6 py-4 hover:bg-[#E04828] active:scale-95 transition-all shadow-[0_8px_20px_rgba(255,92,57,0.25)] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                Send reset link
              </button>
            </form>

            <p className="mt-6 text-center">
              <Link to="/login" data-testid="back-login-link" className="text-[#52526B] font-bold text-sm hover:text-[#FF5C39] inline-flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to login
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
